import { useSignInLogic } from './useSignInLogic'
import { ErrorModal } from './ErrorModal'

export function SignInBoldImmersive() {
  const {
    logoUrl,
    email,
    setEmail,
    isSubmitting,
    error,
    handleSubmit,
    showErrorModal,
    setShowErrorModal,
    errorModalConfig
  } = useSignInLogic()

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 overflow-hidden relative">
      {/* Background glow */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500/8 rounded-full blur-[150px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-purple-500/6 rounded-full blur-[100px]" />
      </div>

      {/* Orbital rings */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        {/* Outer ring */}
        <div className="signin-orbit-1 w-[700px] h-[700px] sm:w-[800px] sm:h-[800px] rounded-full border border-primary-500/[0.07] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          {/* Orbiting dot */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary-500/60 shadow-[0_0_12px_rgba(82,222,69,0.6)]" />
        </div>
        {/* Middle ring */}
        <div className="signin-orbit-2 w-[500px] h-[500px] sm:w-[580px] sm:h-[580px] rounded-full border border-purple-500/[0.08] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-1.5 h-1.5 rounded-full bg-purple-500/60 shadow-[0_0_10px_rgba(216,60,231,0.6)]" />
        </div>
        {/* Inner ring */}
        <div className="signin-orbit-3 w-[340px] h-[340px] sm:w-[400px] sm:h-[400px] rounded-full border border-primary-500/[0.05] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-primary-400/50 shadow-[0_0_8px_rgba(82,222,69,0.5)]" />
        </div>
      </div>

      {/* Center content */}
      <div className="relative z-10 w-full max-w-xl text-center flex flex-col items-center">
        {/* Logo */}
        <div className="mb-6">
          <img
            src={logoUrl}
            alt="OrbitalX"
            className="mx-auto h-32 sm:h-40 w-auto max-w-[420px] sm:max-w-[520px] object-contain drop-shadow-[0_0_50px_rgba(82,222,69,0.3)]"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none'
              e.currentTarget.nextElementSibling?.classList.remove('hidden')
            }}
          />
          <div className="hidden text-white text-4xl font-bold">OrbitalX</div>
        </div>

        {/* Tagline */}
        <p className="text-slate-400 text-sm mb-10 tracking-wide uppercase">
          Mission Control for ABM
        </p>

        {/* Sign-in card */}
        <div className="w-full max-w-sm rounded-2xl border border-white/[0.06] bg-slate-900/80 backdrop-blur-sm p-6 sm:p-8 text-left">
          <h2 className="text-white text-lg font-semibold mb-1">Sign in</h2>
          <p className="text-slate-400 text-sm mb-6">Enter your work email below</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
                placeholder="you@company.com"
                className="w-full rounded-xl border border-slate-700/80 bg-slate-800/60 text-white placeholder-slate-500 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/30 transition-all"
              />
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 disabled:opacity-60 shadow-lg hover:scale-[1.01] active:scale-[0.99] focus:outline-none focus:ring-4 focus:ring-primary-500/25"
              style={{
                background: 'linear-gradient(135deg, #52de45 0%, #3dbf32 50%, #2da825 100%)',
                boxShadow: '0 4px 20px rgba(82, 222, 69, 0.25)'
              }}
            >
              {isSubmitting ? 'Signing in...' : 'Launch'}
            </button>
          </form>
        </div>

        <p className="mt-6 text-xs text-slate-600">
          Enterprise authentication powered by Auth0
        </p>
      </div>

      {showErrorModal && errorModalConfig && (
        <ErrorModal config={errorModalConfig} onClose={() => setShowErrorModal(false)} />
      )}
    </div>
  )
}
